import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { internalApi } from '../utils/api';

// PRD-008 F16 / §7 — the most important visual element in the entire
// console. When an operator is in an impersonation session, every page
// in the customer-facing UI renders with a persistent, high-contrast
// bright-orange banner across the very top. Cannot be dismissed; stays
// for the full session. End-session button calls
// /api/internal/impersonation-sessions/:id/end and clears local state.

function readContext() {
  try {
    const raw = sessionStorage.getItem('operatorImpersonationContext');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function OperatorImpersonationBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(readContext);
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    // Re-check on focus so a session ended in another tab clears here.
    const handler = () => setCtx(readContext());
    window.addEventListener('focus', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('focus', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  if (!ctx || !sessionStorage.getItem('operatorImpersonationToken')) {
    return null;
  }

  const handleEnd = async () => {
    setEnding(true);
    try {
      await internalApi.endImpersonation(ctx.sessionId);
    } catch {
      /* even if the API call fails we still clear the local token —
         the user wants out of the session and the server-side TTL
         will catch up. */
    }
    sessionStorage.removeItem('operatorImpersonationToken');
    sessionStorage.removeItem('operatorImpersonationContext');
    navigate(`/internal/workspaces/${ctx.workspaceId}`);
    window.location.reload();
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[90] bg-orange-500 text-white shadow-md">
      <div className="px-4 py-2 flex items-center gap-3 text-sm">
        <span className="font-semibold uppercase tracking-wide text-xs">
          Operator session
        </span>
        <span className="flex-1 truncate">
          Viewing <strong>{ctx.workspaceName || ctx.workspaceId}</strong> as <strong>{user?.email}</strong>. Every action you take is logged.
        </span>
        <button
          onClick={handleEnd}
          disabled={ending}
          className="px-3 py-1 text-sm font-medium border border-white/80 hover:bg-white hover:text-orange-700 rounded-md disabled:opacity-60"
        >
          {ending ? 'Ending…' : 'End session'}
        </button>
      </div>
    </div>
  );
}
